import { NgClass, NgStyle } from '@angular/common';
import { Component, EventEmitter, Input, IterableDiffer, IterableDiffers, OnDestroy, Optional, Output, ViewChild } from '@angular/core';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { CustomSelectComponent } from 'components/input/custom-select/custom-select.component';
import { DateInputComponent } from 'components/input/date-input/date-input.component';
import { DateTimeInputComponent } from 'components/input/date-time-input/date-time-input.component';
import { FormattedContentDirective } from 'directives/formatted-content.directive';
import { InputType } from 'enums/InputType';
import { InputTypeApprovalModel } from 'models/generated/Models.InputTypeData.InputTypeApproval';
import { InputTypeDateTimeModel } from 'models/generated/Models.InputTypeData.InputTypeDateTime';
import { SelectListItemModel } from 'models/SelectList';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { Subscription } from 'rxjs';
import { InputState, InputStateService } from 'services/input-state.service';
import { WorkspaceService } from 'services/workspace.service';

@Component({
	selector: 'editable-field',
	templateUrl: 'editable-field.component.html',
	styleUrls: ['editable-field.component.scss'],
	imports: [NgClass, FormattedContentDirective, NgStyle, CustomSelectComponent, DateTimeInputComponent, DateInputComponent, CustomInputComponent, TranslatePipe]
})
export class EditableFieldComponent implements OnDestroy {

	@ViewChild(CustomInputComponent) input?: CustomInputComponent;

	public editValue!: string;
	public noAnswer = false;
	private activationSubscription?: Subscription;
	public activationLabel = '';
	defaultIterableDiffer: IterableDiffer<any>;

	@Output() editingChange = new EventEmitter<boolean>();
	private _editing = false;
	set editing(value: boolean) {

		if (value === this._editing) return; // Only react to the value being changed

		if (this.workspaceService && this.workspaceService.activeEditableField &&
			this.workspaceService.activeEditableField!.dirty && value && !this.noActivationState)
			return;

		this._editing = value;
		this.editingChange.emit(value);

		if (value) {
			this.workspaceService.activeEditableField = this;
			this.editValue = this.value.toString();
			setTimeout(() => { if (this.input && this.inputType !== InputType.Editor) this.input.setFocus(); });
		} else if (this.workspaceService.activeEditableField === this) {
			this.workspaceService.activeEditableField = null;
		}

		if (this.inputStateService) {
			switch (value) {
				case true:
					this.inputStateService.focus();
					break;
				case false:
					this.inputStateService.blur();
					break;
			}
		}
	}
	get editing() { return this._editing; }
	get noActivationState() { return this.inputType === InputType.Approval || this.inputType === InputType.Checkbox || this.inputType === InputType.Switch || this.inputType === InputType.Select || this.inputType === InputType.Date || this.inputType === InputType.DateTime; }
	get autoConfirm() { return this.inputType === InputType.Approval || this.inputType === InputType.Checkbox || this.inputType === InputType.Switch || this.inputType === InputType.Select || this.inputType === InputType.Date }
	inputTypeEnum = InputType;

	@Input() enableNoAnswerChoice = false;
	@Input() allowMultiLine = false;
	@Input() inputType: InputType = InputType.Text;
	@Input() maxDate?: Date;

	private _selectList: SelectListItemModel[] = [];
	@Input()
	set selectList(list: SelectListItemModel[]) {
		this._selectList = list;
		this.setActivationLabel();
	}
	get selectList() { return this._selectList; }

	private _placeholder: string = '';
	@Input()
	set placeholder(placeholder: string) {
		this._placeholder = placeholder;
		this.setActivationLabel();
	}
	get placeholder() { return this._placeholder; }

	private _value: string | { date?: Date, dateOnly: boolean } | Date = '';
	@Input()
	set value(value: string | { date?: Date, dateOnly: boolean; } | Date) {
		this._value = value = value ?? '';
		this.editValue = value.toString();
		if (value === '' && this.enableNoAnswerChoice) {
			this.noAnswer = true;
		}
		this.setActivationLabel();
	}
	get value() { return this._value; }

	get valueAsDate(): Date { return this.editing ? new Date(this.editValue) : this.value as Date; }
	get valueAsDateTime() { return this.value as { date?: Date, dateOnly: boolean } | undefined; }


	@Output() save = new EventEmitter<string>();

	constructor(@Optional() private inputStateService: InputStateService, @Optional() private workspaceService: WorkspaceService, iterableDiffers: IterableDiffers, private readonly customDate: CustomDatePipe, private readonly translate: TranslatePipe) {
		if (inputStateService) this.activationSubscription = inputStateService.onActivated(() => { this.activate(); });
		this.defaultIterableDiffer = iterableDiffers.find(this.selectList).create() as IterableDiffer<any>;
	}

	get dirty(): boolean {
		return this.editValue !== this.value;
	}
	get inputState(): InputState | undefined {
		if (this.inputStateService)
			return this.inputStateService.currentState;
		return undefined;
	}

	ngOnDestroy() {
		this.editing = false;
		if (this.activationSubscription) this.activationSubscription.unsubscribe();
	}

	activate(event?: MouseEvent) {
		if (this.inputType == InputType.Switch || this.inputType == InputType.Checkbox) return;
		const selection = window.getSelection();
		if (selection!.toString().length !== 0 ||
			(event && event.currentTarget !== event.target && (event.target as Element).tagName.toLowerCase() === 'a'))
			return;
		this.editing = true;
	}

	dateTimeValueChanged(newValue?: { date?: Date, dateOnly: boolean; }) {
		this.editing = true;
		if (!newValue) {
			// Grundet DateTime er lidt speciel, og ikke under normale omstændigheder skal gemme før der trykkes "ok", men det skal den ved nulstil.
			this.confirm();
		}
		else this.setActivationLabel();
	}

	valueChanged(newValue: string) {
		this.editing = true;
		let confirm = false;
		const previousValue = this.editValue != undefined ? this.editValue : this.value;
		if (this.inputType === InputType.Approval) {
			const oldApproval = (previousValue ? JSON.parse(previousValue as string) : {}) as InputTypeApprovalModel;
			const newApproval = (newValue ? JSON.parse(newValue) : {}) as InputTypeApprovalModel;
			if (oldApproval.approvalStatus != newApproval.approvalStatus)
				confirm = true;
		}
		else if (this.autoConfirm) {
			confirm = true;
		}

		this.editValue = newValue;
		if (newValue !== '') this.noAnswer = false;
		if (confirm)
			this.confirm();
		else
			this.setActivationLabel();
	}

	setActivationLabel() {
		if (this.noAnswer) {
			this.activationLabel = this.translate.transform('general.no-answer');
		} else if (this.value) {
			if (this.inputType === InputType.Select) {
				const item = this.selectList.find(item => item.value == this.value);
				this.activationLabel = item ? item.name : '';
			} else if (this.inputType === InputType.Date)
				this.activationLabel = this.customDate.transform(this.valueAsDate, 'shortDate');
			else if (this.inputType === InputType.DateTime) {
				const dateTime = this.valueAsDateTime;
				this.activationLabel = this.customDate.transform(dateTime?.date, (dateTime?.dateOnly) ? 'shortDate' : '');
			}
			else
				this.activationLabel = this.value.toString();
		} else {
			this.activationLabel = this.placeholder;
		}
	}

	cancel() {
		if (this.input) this.input.setBlur();
		this.editing = false;
		this.editValue = this.value.toString();
		if (this.value === '' && this.enableNoAnswerChoice) {
			this.noAnswer = true;
		}
		this.setActivationLabel();
	}

	blur() {
		if (!this.dirty)
			this.cancel();
	}

	confirm() {
		this.save.emit(this.editValue);
		this.value = this.editValue;
		this.editing = false;
		this.setActivationLabel();
	}

	setNoAnswer() {
		this.noAnswer = true;
		this.editValue = '';
		this.confirm();
	}

	ngDoCheck() {
		let change = this.defaultIterableDiffer.diff(this.selectList);
		if (change) this.setActivationLabel();
	}
}
